import React from 'react';
import { create } from 'zustand';
import { getGroupsForUser, getAppUsersForGroup } from 'src/queries/dashboard';

const allUserOption = { id: 'All Users', username: 'All Users' };

const parseUsernames = (raw) => {
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  try {
    let parsed = JSON.parse(raw);
    if (typeof parsed === 'string') parsed = JSON.parse(parsed);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (e) {
    return {};
  }
};

const defaultSidebarOptions = [
  'wellnessLogs',
  'biometrics',
  // 'fileUpload',
  // 'insights'
];

export const useDashStore = create((set, get) => {
  return {
    loadingNewAppUsers: false,
    sidebarOpen: false,
    setSidebarOpen: (bool) => {
      return set((state) => ({ sidebarOpen: bool }));
    },
    isAdmin: true,

    showAlert: true,
    dismissAlert: () => {
      return set({ showAlert: false });
    },

    /* GROUPS */
    groups: [], // list of groups the user has access to
    groupsFetched: false, // whether the initial group fetch has resolved (vs. genuinely empty)
    setGroups: (newGroups) => {
      return set((state) => ({ groups: newGroups }));
    },
    group: {}, // current selected group
    setGroup: (newGroup) => {
      // set selected group & get/set users for that group
      const { getAppUsers } = get();
      getAppUsers(newGroup.id);
      let newSidebarOptions = [...defaultSidebarOptions];
      if (newGroup.surveys?.includes('nutrition')) {
        newSidebarOptions.push('nutrition');
      }
      if (newGroup.surveys?.includes('exercise')) {
        newSidebarOptions.push('exercise');
      }
      if (newGroup.surveys?.includes('fileUpload')) {
        newSidebarOptions.push('fileUpload');
      }

      set((state) => ({
        group: newGroup,
        loadingNewAppUsers: true,
        sidebarOptions: newSidebarOptions,
      }));
    },
    getGroups: (user) => {
      // get & set initial list of groups
      const { setGroups, setGroup } = get();
      const setOptions = (items) => {
        items.sort((a, b) => a.name - b.name);
        setGroups(items);
        setGroup(items[0] || {});
        set({ groupsFetched: true });
      };
      if (Object.keys(user).length) {
        getGroupsForUser(user, setOptions);
      }
    },

    /* USERS */
    userOptions: [allUserOption],
    selectedUser: allUserOption,
    setSelectedUser: (newUser) => {
      if (newUser.id === 'All Users') {
        // There's no profile for "All Users", and the View Logs toggle is hidden in this
        // view, so leaving viewingProfile on would strand the page on an empty profile
        set((state) => ({ selectedUser: newUser, selectedAppUserData: {}, viewingProfile: false }));
      } else {
        const { appUsers } = get();
        const userData = appUsers.find((user) => user.id === newUser.id);
        set((state) => ({ selectedUser: newUser, selectedAppUserData: userData }));
      }
    },
    appUsers: [],
    selectedAppUserData: {},
    viewingProfile: false,
    setViewingProfile: (newViewingProfile) => {
      return set((state) => ({ viewingProfile: newViewingProfile }));
    },
    getAppUsers: (groupID) => {
      const { setUsers } = get();
      getAppUsersForGroup(groupID, setUsers);
    },
    setUsers: (users, groupID) => {
      const options = users
        .map((user) => {
          const parsed = parseUsernames(user.usernames);
          // Handle both old format ("groupID": "username") and 
          // new DynamoDB format ("groupID": { "S": "username" })
          let username = parsed[groupID];
          if (username && typeof username === 'object' && username.S) {
            username = username.S;
          }
          return {
            id: user.id,
            username: username,
          };
        })
        .filter((user) => user.username) // Filter out users without a username for this group
        .sort((a, b) => a.username.localeCompare(b.username));
      set({
        userOptions: [allUserOption].concat(options),
        selectedUser: allUserOption,
        viewingProfile: false,
        appUsers: users,
        loadingNewAppUsers: false,
      });
    },

    /* PAGES */
    sidebarOptions: defaultSidebarOptions,
    activePage: 'wellnessLogs',
    setActivePage: (newPage) => set((state) => ({ activePage: newPage })),
  };
});
